/**
 * VireoHR - Business Setup Screen
 * Ultra-Minimal Design
 */

import { httpsCallable } from 'firebase/functions';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { functions } from '@/services/cloudFunctions';


const JORDAN_CITIES = [
  'Amman',
  'Zarqa',
  'Irbid',
  'Aqaba',
  'Mafraq',
  'Madaba',
  'Karak',
  'Jerash',
  'Ajloun',
  'Tafileh',
  "Ma'an",
  'Salt'
];

const EMPLOYEE_RANGES = [
  '1-5',
  '6-20',
  '21-50',
  '51-100',
  '100+'
];

export default function SignupBusiness() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Form state
  const [businessName, setBusinessName] = useState('');
  const [city, setCity] = useState('');
  const [employeeRange, setEmployeeRange] = useState('');
  const [hasMultipleBranches, setHasMultipleBranches] = useState(false);
  const [branchName, setBranchName] = useState('Main Branch');

  // Error state
  const [errors, setErrors] = useState({
    businessName: '',
    city: '',
    employeeRange: ''
  });

  const validateForm = (): boolean => {
    const newErrors = {
      businessName: '',
      city: '',
      employeeRange: ''
    };

    if (!businessName.trim()) {
      newErrors.businessName = 'Business name is required';
    }

    if (!city) {
      newErrors.city = 'Please select a city';
    }

    if (!employeeRange) {
      newErrors.employeeRange = 'Please select employee range';
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error !== '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      // Call Cloud Function to create tenant
      const createTenant = httpsCallable(functions, 'createTenant');

      const result = await createTenant({
        businessName: businessName.trim(),
        city,
        employeeRange,
        branchName: hasMultipleBranches ? branchName.trim() : 'Main Branch'
      });

      const data = result.data as { tenantId: string; branchId: string };

      toast.success('Workspace created successfully!');

      // Redirect to owner home with new user flag
      navigate('/owner/home?new=1');

    } catch (error: any) {
      console.error('Create tenant error:', error);

      if (error.code === 'functions/unauthenticated') {
        toast.error('Your session expired. Please log in again.');
        navigate('/login');
      } else if (error.code === 'functions/invalid-argument') {
        toast.error('Please fill in all required fields.');
      } else {
        toast.error('Something went wrong, please try again.');
      }

      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-[#0D9488] mb-2">Set up your company</h1>
          <p className="text-sm text-[#D4AF37]">
            We'll use this to create your workspace.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Business Name */}
          <div>
            <label htmlFor="businessName" className="block text-sm font-medium text-[#D4AF37] mb-1">
              Business Name *
            </label>
            <input
              id="businessName"
              type="text"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              className="w-full px-3 py-2 border border-[#D4AF37] rounded text-sm focus:outline-none focus:ring-1 focus:ring-[#0D9488]"
              disabled={loading}
            />
            {errors.businessName && (
              <p className="text-xs text-red-600 mt-1">{errors.businessName}</p>
            )}
          </div>

          {/* City */}
          <div>
            <label htmlFor="city" className="block text-sm font-medium text-[#D4AF37] mb-1">
              City *
            </label>
            <select
              id="city"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="w-full px-3 py-2 border border-[#D4AF37] rounded text-sm focus:outline-none focus:ring-1 focus:ring-[#0D9488]"
              disabled={loading}
            >
              <option value="">Select a city</option>
              {JORDAN_CITIES.map((cityName) => (
                <option key={cityName} value={cityName}>
                  {cityName}
                </option>
              ))}
            </select>
            {errors.city && (
              <p className="text-xs text-red-600 mt-1">{errors.city}</p>
            )}
          </div>

          {/* Employee Range */}
          <div>
            <label htmlFor="employeeRange" className="block text-sm font-medium text-[#D4AF37] mb-1">
              Number of Employees *
            </label>
            <select
              id="employeeRange"
              value={employeeRange}
              onChange={(e) => setEmployeeRange(e.target.value)}
              className="w-full px-3 py-2 border border-[#D4AF37] rounded text-sm focus:outline-none focus:ring-1 focus:ring-[#0D9488]"
              disabled={loading}
            >
              <option value="">Select range</option>
              {EMPLOYEE_RANGES.map((range) => (
                <option key={range} value={range}>
                  {range}
                </option>
              ))}
            </select>
            {errors.employeeRange && (
              <p className="text-xs text-red-600 mt-1">{errors.employeeRange}</p>
            )}
          </div>

          {/* Multiple Branches Toggle */}
          <div className="pt-2">
            <label className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setHasMultipleBranches(!hasMultipleBranches)}
                disabled={loading}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${hasMultipleBranches ? 'bg-[#0D9488]' : 'bg-[#D4AF37] bg-opacity-30'
                  }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${hasMultipleBranches ? 'translate-x-6' : 'translate-x-1'
                    }`}
                />
              </button>
              <span className="text-sm font-medium text-[#D4AF37]">
                We have multiple branches
              </span>
            </label>
          </div>

          {/* Branch Name (conditional) */}
          {hasMultipleBranches && (
            <div>
              <label htmlFor="branchName" className="block text-sm font-medium text-[#D4AF37] mb-1">
                Main Branch Name
              </label>
              <input
                id="branchName"
                type="text"
                value={branchName}
                onChange={(e) => setBranchName(e.target.value)}
                className="w-full px-3 py-2 border border-[#D4AF37] rounded text-sm focus:outline-none focus:ring-1 focus:ring-[#0D9488]"
                disabled={loading}
              />
            </div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#0D9488] text-white hover:bg-[#0a7d73]"
          >
            {loading ? 'Creating workspace...' : 'Create Workspace'}
          </Button>
        </form>
      </Card>
    </div>
  );
}
